import { useMemo, useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Badge, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { ContentWithCode } from '@components/common/CodeBlock';

function getReviewBlocks(section) {
  const blocks = section?.examples || [];
  return blocks.length > 0 ? blocks : [];
}

export function ReviewContentSession({ course, progress }) {
  const [sectionQueue, setSectionQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const questionPoolScope = course?.metadata?.questionPoolScope || 'unlocked';

  const baseSections = useMemo(() => {
    if (!course?.sections?.length) return [];
    if (questionPoolScope === 'all') {
      return course.sections;
    }
    const unlockedSectionIds = progress?.unlockedSections || [];
    const unlocked = course.sections.filter((section) => unlockedSectionIds.includes(section.id));
    return unlocked.length > 0 ? unlocked : [course.sections[0]];
  }, [course, questionPoolScope, progress?.unlockedSections]);

  const filteredSections = useMemo(() => {
    return baseSections
      .map((section) => ({
        ...section,
        reviewBlocks: getReviewBlocks(section),
      }))
      .filter((section) => section.reviewBlocks.length > 0);
  }, [baseSections]);

  useEffect(() => {
    setSectionQueue(filteredSections);
    setCurrentIndex(0);
  }, [filteredSections]);

  const currentSection = sectionQueue[currentIndex] || null;
  const hasContent = sectionQueue.length > 0;
  const isComplete = hasContent && currentIndex >= sectionQueue.length;

  const handleNextContent = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, sectionQueue.length));
  }, [sectionQueue.length]);

  const handlePreviousContent = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  if (!course || !progress) {
    return (
      <Card className="text-center p-5">
        <p className="text-muted mb-0">Loading review content...</p>
      </Card>
    );
  }

  return (
    <Row className="g-3">
      <Col lg={4}>
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">Review Content Pool</h6>
              <Badge bg={questionPoolScope === 'all' ? 'info' : 'success'}>
                {questionPoolScope === 'all' ? 'All Sections' : 'Unlocked Sections'}
              </Badge>
            </div>

            {baseSections.length === 0 && (
              <Alert variant="warning" className="mt-2">
                No sections available for this review pool.
              </Alert>
            )}

            {filteredSections.length > 0 && (
              <div className="mt-4">
                <div className="d-flex flex-column gap-2">
                  {filteredSections.map((section, index) => (
                    <Button
                      key={`jump-${section.id}`}
                      size="sm"
                      variant={index === currentIndex ? 'primary' : 'outline-primary'}
                      className="text-start"
                      onClick={() => setCurrentIndex(index)}
                    >
                      {section.title}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col lg={8}>
        <Card className="mb-3">
          <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h5 className="mb-1">Review Content</h5>
              <small className="text-muted">
                {hasContent ? `${Math.min(currentIndex + 1, sectionQueue.length)} / ${sectionQueue.length}` : '0 / 0'}
              </small>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={handlePreviousContent}
                disabled={!hasContent || currentIndex === 0}
              >
                Previous
              </Button>
              <Button variant="outline-secondary" onClick={() => setCurrentIndex(0)} disabled={!hasContent}>
                Restart
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleNextContent}
                disabled={!hasContent || currentIndex >= sectionQueue.length - 1}
              >
                Next
              </Button>
            </div>
          </Card.Body>
        </Card>

        {!hasContent && (
          <Card className="text-center p-5">
            <p className="text-muted mb-0">No content blocks match the selected sections.</p>
          </Card>
        )}

        {isComplete && hasContent && (
          <Card className="text-center p-5">
            <h5 className="mb-2">You reached the end of the queue.</h5>
            <p className="text-muted">Restart to review the content again.</p>
            <Button variant="primary" onClick={() => setCurrentIndex(0)}>
              Restart Review
            </Button>
          </Card>
        )}

        {currentSection && !isComplete && (
          <Card className="shadow-sm">
            <Card.Body>
              <div className="mb-3">
                <h6 className="text-muted mb-1">Section</h6>
                <h4 className="mb-0">{currentSection.title}</h4>
              </div>
              <div className="d-flex flex-column gap-4">
                {currentSection.reviewBlocks.map((block) => (
                  <Card key={block.id} className="border-0 bg-light">
                    <Card.Body>
                      <h5 className="mb-2">{block.title}</h5>
                      {/* F1 (PRD): syntax-highlighted code blocks via ContentWithCode */}
                      <ContentWithCode content={block.content} className="content-text" />
                    </Card.Body>
                  </Card>
                ))}
              </div>
              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={handlePreviousContent}
                  disabled={currentIndex === 0}
                >
                  Previous Section
                </Button>
                <Button
                  variant="success"
                  onClick={handleNextContent}
                  disabled={currentIndex >= sectionQueue.length - 1}
                >
                  Next Section
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
}

ReviewContentSession.propTypes = {
  course: PropTypes.object,
  progress: PropTypes.object,
};
